//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { Opaque } from '@utils/opaque';

export enum ContentSourceProductsSortBy {
  GreatestNewRating = 'GREATEST_NEW_RATING',
  MostContentSourceMedia = 'MOST_CS_MEDIA',
  MostRecentContentSourceReviews = 'MOST_RECENT_CS_REVIEWS',
  MostTotalContentSourceReviews = 'MOST_TOTAL_CS_REVIEWS',
  OpportunityScore = 'OPPORTUNITY_SCORE',
  PageViews = 'PAGE_VIEWS',
  ProductName = 'PRODUCT_NAME',
  RatingImpact = 'RATING_IMPACT',
}

export enum ContentSourceSortDirection {
  Ascending = 'asc',
  Descending = 'desc',
}

export type CasProductId = Opaque<string, 'CasProductId'>;

export interface ContentSourceMetric {
  /**
   * The metric value available for purchase via ContentSource. This could represent a count (e.g., number of reviews)
   * or an average (e.g., average rating).
   */
  contentSourceValue?: number;
  /**
   * The current metric value as it exists in the client's Bazaarvoice (BV) instance. This provides a baseline for
   * comparison.
   */
  bazaarvoiceValue?: number;
  /**
   * The projected new metric value if the ContentSource data is purchased. For counts, this is typically
   * contentSourceValue + bazaarvoiceValue. For averages, it's the new combined average.
   */
  combinedValue?: number;
  /**
   * The difference between combinedValue and bazaarvoiceValue. This field is primarily applicable for metrics like
   * average ratings where a delta is meaningful. It will be undefined for count-based metrics or if not applicable.
   * @example For average rating: combinedValue (e.g., 4.5) - bazaarvoiceValue (e.g., 4.2) = delta (e.g., 0.3)
   */
  delta?: number;
}

export interface ContentSourceProduct {
  averageRating?: ContentSourceMetric;
  bvProductId?: string;
  casProductId?: CasProductId;
  name?: string;
  opportunityScore?: number;
  pageViews?: number;
  recentReviewCount?: ContentSourceMetric;
  thumbnailUrl?: string;
  totalMediaCount?: ContentSourceMetric;
  totalReviewCount?: ContentSourceMetric;
  status?: ContentSourceProductStatus;
}

export enum ContentSourceProductStatus {
  None = 'NONE',
  Ordered = 'ORDERED',
  InCart = 'IN_CART',
}

export enum ContentSourceTaskStatus {
  Enqueued = 'enqueued',
  Processing = 'processing',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Canceled = 'canceled',
}

export interface ContentSourceProductsFilters {
  productStatus?: ContentSourceProductStatus[];
}

export interface ContentSourceProductsRequest {
  limit?: number;
  offset?: number;
  searchText?: string;
  sort?: ContentSourceProductsSortBy;
  sortOrder?: ContentSourceSortDirection;
  filters?: ContentSourceProductsFilters;
}

export interface ContentSourceProductsResponse {
  results: ContentSourceProduct[];
  total: number;
}

export enum ContentSourceProductReviewsSortBy {
  DateCreated = 'DATE_CREATED',
}

export interface ContentSourceEligibleReviewsByProductRequest {
  casProductId: string;
  limit?: number;
  offset?: number;
  sortBy?: ContentSourceProductReviewsSortBy;
  sortOrder?: ContentSourceSortDirection;
}

export interface ContentSourceProductReview {
  body?: string;
  casReviewId?: string;
  submissionTime?: Date;
  rating?: number;
  title?: string;
  userNickname?: string;
  userPhotoUrl?: string;
}

export interface ContentSourceEligibleReviewsByProductResponse {
  results?: ContentSourceProductReview[];
  total?: number;
}

export enum ContentSourceProductMediaSortBy {
  MostRecent = 'MOST_RECENT',
}

export interface ContentSourceProductReviewMediaRequest {
  casProductId: string;
  limit?: number;
  offset?: number;
  sortBy?: ContentSourceProductMediaSortBy;
  sortOrder?: ContentSourceSortDirection;
}

export interface ContentSourceProductMedia {
  casMediaId?: string;
  casReviewId?: string;
  type?: 'Photo' | 'Video';
  mediaUrl?: string;
}

export interface ContentSourceProductReviewMediaResponse {
  results?: ContentSourceProductMedia[];
  total?: number;
}

export interface ContentSourceCartSummary {
  productCount?: number;
  reviewCount?: number;
  mediaCount?: number;
  estimatedSetupTimeDays?: number;
  estimatedDisplayDate?: number;
}

export interface ContentSourceCartSummaryResponse {
  data?: ContentSourceCartSummary;
}

export interface ContentSourceRemoveProductFromCartRequest {
  casProductId: CasProductId;
}

export interface ContentSourceRemoveProductFromCartResponse {
  taskUid?: number;
}

export interface ContentSourceTaskDetailsResponse {
  taskUid?: number;
  status?: ContentSourceTaskStatus;
}

export interface ContentSourceTaskDetailsRequest {
  taskUid?: number;
}

export interface ContentSourceAddProductsToCartRequest {
  casProductIds?: CasProductId[];
}

export interface ContentSourceCartTaskResponse {
  taskUid?: number;
}
